import { useEffect, useRef } from "react";
import type { ReactNode, RefObject } from "react";
import type { ModeDefinition } from "@picnic-field/local-host/protocol";
import type { PaletteLike } from "@picnic-field/renderer/appearance";
import { Button } from "../../../components/ui/Button";
import { ModelPreview } from "../../../components/ModelPreview";
import { ReadAloudButton } from "../../../components/ReadAloud";
import { useDensity } from "../../../store/density";
import { hasMessage, useI18n } from "../../../i18n";
import type { PicoShown } from "../../pico";
import { hintText, surfaceKey } from "../lib/hints";
import { formatClock } from "../lib/progress";
import { presetSummary, presetTitle } from "../lib/presets";
import type { ResultView, GhostCandidate, SaveState } from "../types";
import { STATIC_TEXT } from "../../../lib/staticReason";
import { MissionBand } from "./MissionPanels";
import type { StaticReason } from "../../../lib/staticReason";

type Preset = ModeDefinition["presets"][number];

const PRESET_ICON: Record<string, string> = {
  easy: "🛣",
  medium: "⛰",
  hard: "🏔",
  "line-easy": "〰",
  "line-medium": "↰",
  walk: "🐾",
};

export const presetIcon = (p: Preset) =>
  typeof p.icon === "string" ? p.icon : (PRESET_ICON[p.key] ?? "🏁");

// ── FS-04-01 코스 고르기 띠 ────────────────────────────────────────────────

/**
 * 코스 카드 3 + 고스트 요약(이름·완주 여부만 — 시간 없음, SCR v0.6 개정 3).
 * SE: '코스 고르기' 제목 줄 + 아이콘 카드 3(64px) + '고스트: 부기 ✓ · 또또 ✓ · (＋)'
 * SU: '코스' + 텍스트 카드 3 + '고스트' + 칩('부기 완주 ✓' · '또또 미완주') + ＋
 */
export function CourseStrip({
  presets,
  presetKey,
  onPreset,
  ghosts,
  selected,
  onOpenPicker,
  pickerOpen,
  disabled,
  unavailable = [],
  pick,
}: {
  /**
   * 고르기 화면(2026-10-09) — 코스 카드 3 대신 <b>지금 코스 한 줄 + [바꾸기]</b>(코스 고르기 대화상자 CoursePickDialog 를 연다).
   * mission: 지금 코스가 오늘의 미션 코스인가(here — 🎯 칩) · 미션이 다른 코스에 있는가(other — 안내는 미션 줄 MissionSummary) · 미션 없음(null)
   */
  pick?: { open: boolean; onOpen: () => void; mission: "here" | "other" | null };
  /** 커널이 받지 못한 고스트(입력도 위치 트랙도 없음 — FE-501 로 빠짐) */
  unavailable?: readonly string[];
  presets: readonly Preset[];
  presetKey: string;
  onPreset: (k: string) => void;
  ghosts: readonly GhostCandidate[];
  selected: readonly string[];
  onOpenPicker: () => void;
  pickerOpen: boolean;
  disabled?: boolean;
}) {
  const { t } = useI18n();
  const { density } = useDensity();
  const se = density === "se";
  const chosen = selected
    .map((id) => ghosts.find((g) => g.ghostId === id))
    .filter((g): g is GhostCandidate => !!g);

  const current = presets.find((p) => p.key === presetKey);
  const currentSummary = current ? presetSummary(t, current) : "";
  const summary = pick ? (
    <div className="pf-course__current" data-testid="course-current" data-current={presetKey}>
      <span className="pf-course__current-name">
        {current ? `${presetIcon(current)} ${presetTitle(t, current)}` : presetKey}
        {currentSummary && <span className="pf-course__current-sub"> · {currentSummary}</span>}
      </span>
      {pick.mission === "here" && (
        <span className="pf-chip-static pf-chip-static--ok" data-testid="course-current-mission">
          🎯 {t("course.mission.title")}
        </span>
      )}
      <Button
        variant="accent"
        className="pf-course__change"
        aria-haspopup="dialog"
        aria-expanded={pick.open}
        disabled={disabled}
        onClick={pick.onOpen}
        data-testid="course-pick-open"
      >
        {t("course.strip.change")}
      </Button>
    </div>
  ) : null;
  const cards = (
    <div className="pf-course__presets" role="radiogroup" aria-label={t("course.strip.presets")}>
      {presets.map((p) => {
        const on = p.key === presetKey;
        return (
          <Button
            key={p.key}
            role="radio"
            aria-checked={on}
            variant={on ? "accent" : "ghost"}
            className="pf-course__preset"
            data-preset={p.key}
            disabled={disabled}
            onClick={() => onPreset(p.key)}
          >
            {se ? `${presetIcon(p)} ${presetTitle(t, p)}` : presetTitle(t, p)}
          </Button>
        );
      })}
    </div>
  );

  if (se) {
    const list = chosen.length
      ? chosen
          .map((g) =>
            unavailable.includes(g.ghostId)
              ? t("course.ghost.unavailable", { name: g.label })
              : g.completed
                ? `${g.label} ✓`
                : g.label,
          )
          .join(" · ")
      : t("course.ghost.noneShort");
    return (
      <div className="pf-pane pf-course__strip pf-course__strip--se" data-testid="course-strip">
        <h2 className="pf-course__strip-title">{t("course.strip.titleSE")}</h2>
        {summary}
        <div className="pf-course__strip-row">
          {!summary && cards}
          <button
            type="button"
            className="pf-course__ghostline"
            aria-expanded={pickerOpen}
            aria-controls="course-ghost-picker"
            disabled={disabled}
            onClick={onOpenPicker}
            data-testid="ghost-open"
          >
            {t("course.ghost.lineSE", { list })}
          </button>
        </div>
      </div>
    );
  }
  return (
    <div className="pf-pane pf-course__strip pf-course__strip--su" data-testid="course-strip">
      {summary && <h2 className="pf-course__strip-title">{t("course.strip.titleSU")}</h2>}
      {summary}
      <div className="pf-course__strip-row">
        {!summary && <h2 className="pf-course__strip-title">{t("course.strip.titleSU")}</h2>}
        {!summary && cards}
        <span className="pf-muted pf-course__ghost-label">{t("course.ghost.label")}</span>
        <ul className="pf-course__ghost-chips" aria-label={t("course.ghost.label")}>
          {chosen.length === 0 && <li className="pf-chip-static">{t("course.ghost.noneShort")}</li>}
          {chosen.map((g) => (
            <li
              key={g.ghostId}
              className="pf-chip-static"
              data-testid="ghost-chip"
              data-unavailable={unavailable.includes(g.ghostId) || undefined}
            >
              {unavailable.includes(g.ghostId)
                ? t("course.ghost.unavailable", { name: g.label })
                : t(g.completed ? "course.ghost.chipDone" : "course.ghost.chipNotDone", {
                    name: g.label,
                  })}
            </li>
          ))}
        </ul>
        <Button
          variant="ghost"
          className="pf-course__add"
          aria-label={t("course.ghost.add")}
          aria-expanded={pickerOpen}
          aria-controls="course-ghost-picker"
          disabled={disabled}
          onClick={onOpenPicker}
          data-testid="ghost-open"
        >
          ＋
        </Button>
      </div>
    </div>
  );
}

/**
 * 고르기 대화상자 틀(2026-10-09 — 코스 고르기 · 미션 고르기가 같이 쓴다). ✕ · Esc · 바깥 누르기로 닫는다. 열면 `focusRef` 에 포커스.
 */
function PickDialog({
  title,
  testId,
  onClose,
  focusRef,
  children,
}: {
  title: string;
  testId: string;
  onClose: () => void;
  focusRef?: RefObject<HTMLButtonElement | null>;
  children: ReactNode;
}) {
  const { t } = useI18n();
  const { density } = useDensity();
  const close = useRef(onClose);
  useEffect(() => {
    close.current = onClose;
  });
  useEffect(() => {
    focusRef?.current?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.stopPropagation();
        close.current();
      }
    };
    window.addEventListener("keydown", onKey, true);
    return () => window.removeEventListener("keydown", onKey, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return (
    <div
      className="pf-modal"
      data-testid={testId}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        className={`pf-pane pf-modal__pane pf-course__pick pf-course__pick--${density}`}
        role="dialog"
        aria-modal="true"
        aria-labelledby={`${testId}-title`}
      >
        <div className="pf-course__pick-head">
          <h2 id={`${testId}-title`} className="pf-h2">
            {title}
          </h2>
          <Button variant="ghost" icon="✕" onClick={onClose} data-testid={testId.replace(/-dialog$/, "-close")}>
            {t("common.close")}
          </Button>
        </div>
        {children}
      </div>
    </div>
  );
}

/** 코스 고르기 대화상자(2026-10-09 · FS-04-01) — 모든 코스 큰 카드(아이콘 · 이름 · 장애물 요약). 고르면 닫힌다. 미션은 따로(MissionPickDialog) */
export function CoursePickDialog({
  presets,
  presetKey,
  onPick,
  onClose,
}: {
  presets: readonly Preset[];
  presetKey: string;
  onPick: (k: string) => void;
  onClose: () => void;
}) {
  const { t } = useI18n();
  const current = useRef<HTMLButtonElement>(null);
  return (
    <PickDialog
      title={t("course.strip.titleSE")}
      testId="course-pick-dialog"
      onClose={onClose}
      focusRef={current}
    >
      <div
        className="pf-course__pick-grid"
        role="radiogroup"
        aria-label={t("course.strip.presets")}
      >
        {presets.map((p) => {
          const on = p.key === presetKey;
          const sub = presetSummary(t, p);
          return (
            <Button
              key={p.key}
              ref={on ? current : undefined}
              role="radio"
              aria-checked={on}
              variant={on ? "accent" : "ghost"}
              className="pf-course__pick-card"
              data-preset={p.key}
              onClick={() => onPick(p.key)}
            >
              <span className="pf-course__pick-icon" aria-hidden="true">
                {presetIcon(p)}
              </span>
              <span className="pf-course__pick-title">{presetTitle(t, p)}</span>
              {sub && <span className="pf-course__pick-sub">{sub}</span>}
            </Button>
          );
        })}
      </div>
    </PickDialog>
  );
}

/** 미션 고르기 대화상자(2026-10-09) — 오늘의 미션 목표들(MissionCard). 목표를 누르면 그 코스를 고르고 닫힌다 */
export function MissionPickDialog({
  onClose,
  children,
}: {
  onClose: () => void;
  children: ReactNode;
}) {
  const { t } = useI18n();
  return (
    <PickDialog title={t("course.mission.pick")} testId="mission-pick-dialog" onClose={onClose}>
      {children}
    </PickDialog>
  );
}

/** 고스트 고르기 — 친구 리플레이 ≤max, 이름·완주 여부만(시간·순위 없음 · SR-LCL-03) */
export function GhostPicker({
  ghosts,
  selected,
  max,
  onToggle,
  onClose,
}: {
  ghosts: readonly GhostCandidate[];
  selected: readonly string[];
  max: number;
  onToggle: (id: string) => void;
  onClose: () => void;
}) {
  const { t } = useI18n();
  const first = useRef<HTMLButtonElement>(null);
  useEffect(() => first.current?.focus(), []);
  const full = selected.length >= max;
  return (
    <div
      className="pf-pane pf-course__picker"
      id="course-ghost-picker"
      role="dialog"
      aria-labelledby="ghost-picker-title"
      data-testid="ghost-picker"
      onKeyDown={(e) => {
        if (e.key === "Escape") {
          e.stopPropagation();
          onClose();
        }
      }}
    >
      <h2 id="ghost-picker-title" className="pf-h2">
        {t("course.ghost.pickerTitle", { max })}
      </h2>
      {ghosts.length === 0 ? (
        <p className="pf-muted" data-testid="ghost-empty">
          {t("course.ghost.none")}
        </p>
      ) : (
        <ul className="pf-course__picker-list">
          {ghosts.map((g, i) => {
            const on = selected.includes(g.ghostId);
            return (
              <li key={g.ghostId}>
                <Button
                  ref={i === 0 ? first : undefined}
                  variant={on ? "accent" : "ghost"}
                  aria-pressed={on}
                  disabled={!on && full}
                  icon="👻"
                  className="pf-course__picker-item"
                  data-ghost={g.ghostId}
                  onClick={() => onToggle(g.ghostId)}
                >
                  {t(g.completed ? "course.ghost.chipDone" : "course.ghost.chipNotDone", {
                    name: g.label,
                  })}
                </Button>
              </li>
            );
          })}
        </ul>
      )}
      {full && <p className="pf-muted">{t("course.ghost.max", { max })}</p>}
      <Button variant="primary" icon="✓" onClick={onClose} data-testid="ghost-done">
        {t("course.ghost.pickDone")}
      </Button>
    </div>
  );
}

// ── FS-04-01 오른쪽: 내 기계 미리보기 · 출발 ───────────────────────────────────

export function ReadyPane({
  glbUrl,
  appearance,
  palette,
  ghostCount,
  staticWork,
  staticWhy = "noProgram",
  ready,
  onStart,
}: {
  glbUrl: string | null;
  appearance: Parameters<typeof ModelPreview>[0]["appearance"];
  palette: PaletteLike | null;
  ghostCount: number;
  staticWork: boolean;
  /** 움직이지 않는 이유 — 안내 아래 한 줄(달리려면 할 일). 모르면 일반 문구(프로그램 없음) */
  staticWhy?: StaticReason;
  ready: boolean;
  onStart: () => void;
}) {
  const { t } = useI18n();
  const { density } = useDensity();
  return (
    <aside className="pf-pane pf-pane--hero pf-course__side" data-testid="course-ready">
      <h2 className="pf-h2">{t("course.side.myMachine")}</h2>
      <ModelPreview
        className="pf-course__preview"
        glbUrl={glbUrl}
        appearance={appearance}
        palette={palette}
        label={t("course.side.previewLabel")}
        quality="low"
      />
      <p className="pf-muted" data-testid="ghost-state">
        {ghostCount
          ? t("course.ghost.selectedCount", { n: ghostCount })
          : t("course.ghost.noneSelected")}
      </p>
      {staticWork && (
        <p className="pf-course__note" data-testid="static-note" data-reason={staticWhy}>
          {t("course.side.static")}
          <br />
          <span data-testid="static-why">{t(STATIC_TEXT[staticWhy].short)}</span>
        </p>
      )}
      <div className="pf-course__side-end">
        <Button
          variant="accent"
          block
          icon={density === "se" ? "🏁" : undefined}
          disabled={!ready}
          onClick={onStart}
          data-testid="course-start"
        >
          {t("course.side.start")}
        </Button>
      </div>
    </aside>
  );
}

// ── FS-04-03 결과 ───────────────────────────────────────────────────────────

/** 힌트 문구의 [강조] 구간을 밑줄로(SE 와이어 '천천히 출발해 봐') — 문구는 카탈로그 키에서만 온다 */
function rich(text: string): { plain: string; node: ReactNode } {
  const parts = text.split(/\[([^\]]+)\]/);
  return {
    plain: parts.join(""),
    node: parts.map((p, i) => (i % 2 ? <u key={i}>{p}</u> : p)),
  };
}

export function ResultPane({
  view,
  onRetry,
  onFixCode,
  onAssembly,
  onGallery,
  onRetrySave,
  onWatch,
}: {
  view: ResultView;
  onRetry: () => void;
  onFixCode: () => void;
  onAssembly: () => void;
  onGallery: () => void;
  onRetrySave?: () => void;
  /** 저장된 리플레이 보기(FS-14-01) — 저장 전이면 null */
  onWatch?: (() => void) | null;
}) {
  const { t, locale } = useI18n();
  const { density } = useDensity();
  const se = density === "se";
  const { result } = view;
  const time = formatClock(result.timeMs / 1000, !se);
  // 미완주: 규칙 힌트(속도·출력 어휘) — 없으면(연속 반복 금지) 피코 반응 문구
  const bubble =
    !result.completed && view.hint
      ? hintText(t, locale, density, view.hint, (k) => hasMessage(k, locale))
      : view.pico.shownText;
  const hint = rich(bubble);
  const ref = useRef<HTMLHeadingElement>(null);
  useEffect(() => ref.current?.focus(), []);

  return (
    <aside
      className={`pf-pane pf-pane--hero pf-course__side pf-course__result`}
      data-testid="course-result"
      data-completed={result.completed}
      data-reason={result.reason}
    >
      <h2 ref={ref} tabIndex={-1} className="pf-h2">
        {t("run.result.title")}
      </h2>
      <div className="pf-course__result-row">
        <div className="pf-course__stamp" data-testid="result-stamp">
          {t(result.completed ? "run.result.done" : "run.result.notDone")}
        </div>
        {se ? (
          <div>
            <span className="pf-chip-static" data-testid="result-time">
              {t("run.result.timeChip", { time })}
            </span>{" "}
            <span className="pf-chip-static" data-testid="result-attempts">
              {t("run.result.attemptChip", { n: result.attempts })}
            </span>
            <p className="pf-muted">{t("run.result.onlyMine")}</p>
          </div>
        ) : (
          <table className="pf-course__table">
            <tbody>
              <tr>
                <td>{t("run.result.time")}</td>
                <td data-testid="result-time">
                  <b>{time}</b>
                </td>
              </tr>
              <tr>
                <td>{t("run.result.attempts")}</td>
                <td data-testid="result-attempts">
                  <b>{t("run.result.attemptsValue", { n: result.attempts })}</b>
                </td>
              </tr>
              {view.stoppedAt && (
                <tr>
                  <td>{t("run.result.stoppedAt")}</td>
                  <td data-testid="result-stopped">
                    {t("run.result.stoppedValue", {
                      surface: t(`run.surface.${surfaceKey(view.stoppedAt.surface)}`),
                      pct: view.stoppedAt.pct,
                    })}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        )}
      </div>
      <div
        className={`pf-pico${view.pico.animation === "cheer" ? " pf-pico--cheer" : ""}`}
        data-pico={result.completed ? "cheer" : view.hint ? "hint" : "react"}
        data-hint={view.hint?.key ?? ""}
        data-hint-pattern={view.hint?.pattern ?? ""}
        data-pico-event={view.pico.event}
        data-pico-source={view.pico.source}
      >
        <div className="pf-pico__avatar" aria-hidden="true">
          {view.pico.avatar}
        </div>
        <p
          className="pf-pico__bubble pf-course__bubble"
          role="status"
          aria-live="polite"
          data-read-aloud={se ? "" : undefined}
          data-testid="result-hint"
        >
          {hint.node}
        </p>
        <ReadAloudButton text={hint.plain} />
      </div>
      {view.mission && <MissionBand view={view.mission} />}
      <ReplaySaveBanner
        state={view.save.state}
        onRetry={onRetrySave}
        onWatch={onWatch ?? null}
        pico={view.savedPico ?? null}
      />
      <div className="pf-course__actions">
        <Button variant="accent" icon={se ? "⟲" : undefined} onClick={onRetry} data-testid="retry">
          {t("run.action.retry")}
        </Button>
        <Button variant="ghost" icon={se ? "🧩" : undefined} onClick={onFixCode}>
          {t(se ? "run.action.fixCodeSE" : "run.action.fixCodeSU")}
        </Button>
        <Button variant="ghost" icon={se ? "🔧" : undefined} onClick={onAssembly}>
          {t(se ? "run.action.assemblySE" : "run.action.assemblySU")}
        </Button>
        <Button variant="ghost" icon={se ? "🏛" : undefined} onClick={onGallery}>
          {t("run.action.gallery")}
        </Button>
      </div>
    </aside>
  );
}

/**
 * 리플레이 저장 상태(FS-04-03 구성 요소 · 와이어 '리플레이 저장됨 ✓'). 배지는 PH1 에 없다(SR-ACH-02 — 서버 권위 PH2).
 * saving · saved(▶ 다시 보기 → FS-14-01) · offline(이 기기에 보관 — 연결되면 자동) · failed(다시 저장) · tooBig · none(표시 없음)
 */
function ReplaySaveBanner({
  state,
  onRetry,
  onWatch,
  pico,
}: {
  state: SaveState;
  onRetry?: () => void;
  onWatch: (() => void) | null;
  pico: PicoShown | null;
}) {
  const { t } = useI18n();
  const { density } = useDensity();
  if (state === "none") return null;
  const tone =
    state === "saved" ? "ok" : state === "offline" || state === "failed" ? "warn" : "info";
  const icon =
    state === "saved" && pico
      ? pico.avatar
      : { saving: "💾", saved: "💾", offline: "📶", failed: "💾", tooBig: "💾" }[state];
  return (
    <div
      className={`pf-banner pf-banner--${tone} pf-course__save`}
      role="status"
      aria-live="polite"
      data-testid="replay-state"
      data-replay={state}
      data-pico-event={state === "saved" && pico ? pico.event : undefined}
    >
      <p className="pf-banner__text">
        <span aria-hidden="true">{icon} </span>
        {t(`run.save.${state}.${density}`)}
      </p>
      {state === "failed" && onRetry && (
        <Button
          variant="ghost"
          className="pf-btn--sm"
          icon="⟲"
          onClick={onRetry}
          data-testid="replay-retry"
        >
          {t("run.save.retry")}
        </Button>
      )}
      {state === "saved" && onWatch && (
        <Button
          variant="ghost"
          className="pf-btn--sm"
          icon="▶"
          onClick={onWatch}
          data-testid="replay-watch"
        >
          {t("run.save.watch")}
        </Button>
      )}
    </div>
  );
}
